/**
 * What the parent has actually promised.
 *
 * Interest is the most dangerous feature in a product like this, because the
 * failure mode is invisible at the moment of the mistake. A parent sets 10% a
 * week because it makes the number move fast enough to feel exciting; a $20
 * balance is over $1,000 within a year and the bill arrives long after anyone
 * remembers choosing the rate.
 *
 * So no interest rule gets saved without showing this first. One number, in the
 * parent's own money: at this rate, if they keep saving like this, you owe about
 * $X by next August.
 *
 * Deposits and growth are tracked separately throughout — the plan requires
 * projections to distinguish them, and it is also the only honest way to show a
 * child that most of an early balance is money they put in, not money that
 * appeared.
 */

export const RATE_BASIS = Object.freeze({
  annual: 1,
  monthly: 12,
  weekly: 52,
});

/** Anything above this deserves a warning rather than a straight face. */
export const PLAUSIBLE_ANNUAL_RATE_PCT = 12;

export function toAnnualRatePct(rate, basis = 'annual') {
  const periods = RATE_BASIS[basis] ?? 1;
  return (Math.pow(1 + rate / 100, periods) - 1) * 100;
}

/**
 * Month-by-month projection of the parent's outstanding obligation.
 *
 * Interest compounds monthly on the balance at the start of the month, and a
 * per-period cap in absolute dollars is applied before compounding. The cap is
 * in dollars rather than as a rate for the reason the rate cap fails: a
 * percentage ceiling stops bounding anything once the balance is large.
 */
export function projectLiability({
  balanceCents = 0,
  weeklyDepositCents = 0,
  annualRatePct = 0,
  monthlyCapCents = null,
  matchRatePct = 0,
  months = 12,
}) {
  const monthlyRate = annualRatePct / 100 / 12;
  const monthlyDeposit = Math.round((weeklyDepositCents * 52) / 12);

  let principal = balanceCents;
  let growth = 0;
  const series = [{ month: 0, principalCents: principal, growthCents: 0, totalCents: principal }];

  for (let month = 1; month <= months; month += 1) {
    const opening = principal + growth;
    let earned = Math.round(opening * monthlyRate);
    if (monthlyCapCents != null) earned = Math.min(earned, monthlyCapCents);

    const matched = Math.round(monthlyDeposit * (matchRatePct / 100));

    principal += monthlyDeposit;
    growth += earned + matched;
    series.push({
      month,
      principalCents: principal,
      growthCents: growth,
      totalCents: principal + growth,
      earnedThisMonthCents: earned,
      matchedThisMonthCents: matched,
    });
  }

  const final = series[series.length - 1];
  return {
    series,
    depositedCents: final.principalCents - balanceCents,
    growthCents: final.growthCents,
    totalCents: final.totalCents,
    /** What the parent funds out of pocket beyond the child's own earnings. */
    parentFundedCents: final.growthCents,
    implausible: annualRatePct > PLAUSIBLE_ANNUAL_RATE_PCT,
  };
}

/**
 * The number that belongs on the parent's home screen.
 *
 * Every balance in this app is a promise, not a deposit, and the plan had no
 * representation of the promise in aggregate. Without it, balances only ever
 * grow and the ledger drifts from what the parent believes they owe.
 */
export function outstandingObligation(balancesByChild) {
  return Object.values(balancesByChild).reduce((sum, cents) => sum + cents, 0);
}
